import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Image as ImageIcon,
  Save,
  Eye,
  Plus,
  Trash2,
  CheckCircle,
  Loader2,
  ExternalLink,
  Home,
  User,
  Wrench,
  ArrowRightLeft,
  GripVertical,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { RichTextEditor } from "./RichTextEditor";
import { ImageUploader } from "./ImageUploader";
import { AIWordingButton } from "./AIWordingButton";

const API = `${window.location.origin}/api`;

// Slugify helper — mirrors backend logic so the UI preview matches the persisted slug
const slugify = (s) => (s || "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "")
  .slice(0, 80) || "untitled";

// Block metadata — human labels + which editor type to render
const PAGE_BLOCKS = {
  landing: {
    label: "Landing page",
    icon: Home,
    publicRoute: "/",
    blocks: [
      { key: "landing.hero_headline", label: "Hero headline", type: "text", helper: "The big opening line on the homepage." },
      { key: "landing.hero_subheadline", label: "Hero sub-headline", type: "text", helper: "Supporting line under the hero headline." },
      { key: "landing.about_preview", label: "About preview", type: "html", helper: "Short about blurb shown on the homepage. Formatting supported." },
      { key: "landing.services_intro", label: "Services intro", type: "text", helper: "Short lead-in shown above the services grid." },
      { key: "landing.contact_message", label: "Contact message", type: "html", helper: "Message shown in the contact section." },
    ],
  },
  about: {
    label: "About page",
    icon: User,
    publicRoute: "/about",
    blocks: [
      { key: "about.hero_headline", label: "Hero headline", type: "text" },
      { key: "about.hero_subheadline", label: "Hero sub-headline", type: "text" },
      { key: "about.intro_paragraph", label: "Intro paragraph", type: "html" },
      { key: "about.mission_statement", label: "Mission statement", type: "html" },
    ],
  },
};

const GALLERY_CATEGORIES = [
  { value: "carpentry", label: "Carpentry" },
  { value: "general_repairs", label: "General Repairs" },
  { value: "furniture_builds", label: "Furniture Builds" },
  { value: "cnc_factory", label: "CNC / Factory" },
  { value: "other", label: "Other" },
];


export const SiteEditorTab = ({ authHeaders }) => {
  const [section, setSection] = useState("landing"); // landing | about | gallery
  const [blocks, setBlocks] = useState({}); // key → { value, type, is_default }
  const [dirty, setDirty] = useState({});   // key → new value
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site/content`, authHeaders);
      setBlocks(res.data.blocks || {});
      setDirty({});
    } catch (e) {
      toast.error("Failed to load site content");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleBlockChange = (key, value) => {
    setDirty((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const entries = Object.entries(dirty);
    if (!entries.length) {
      toast.info("No changes to save.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        blocks: entries.map(([key, value]) => {
          const [page, ...rest] = key.split(".");
          const section_key = rest.join(".");
          const existing = blocks[key];
          return {
            page,
            section_key,
            value,
            type: existing?.type || "text",
          };
        }),
      };
      await axios.put(`${API}/admin/site/content`, payload, authHeaders);
      toast.success(`Saved ${entries.length} block${entries.length > 1 ? "s" : ""}.`);
      await fetchContent();
    } catch (e) {
      console.error(e);
      toast.error("Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const currentConfig = PAGE_BLOCKS[section];
  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-6" data-testid="site-editor-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold font-heading">Site Editor</h2>
          <p className="text-sm text-muted-foreground">Edit the public website. Changes go live as soon as you save.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => {
            const route = section === "gallery" ? "/gallery"
              : section === "services" ? "/#services"
              : currentConfig?.publicRoute || "/";
            window.open(route, "_blank");
          }} data-testid="site-editor-preview-btn">
            <ExternalLink className="h-4 w-4 mr-1" />
            Preview
          </Button>
          {section !== "gallery" && section !== "services" && (
            <Button onClick={handleSave} disabled={saving || dirtyCount === 0} data-testid="site-editor-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
            </Button>
          )}
        </div>
      </div>

      {/* Section switcher */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(PAGE_BLOCKS).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <Button
              key={key}
              variant={section === key ? "default" : "outline"}
              size="sm"
              onClick={() => setSection(key)}
              className="gap-2"
              data-testid={`site-editor-section-${key}`}
            >
              <Icon className="h-4 w-4" />
              {cfg.label}
            </Button>
          );
        })}
        <Button
          variant={section === "services" ? "default" : "outline"}
          size="sm"
          onClick={() => setSection("services")}
          className="gap-2"
          data-testid="site-editor-section-services"
        >
          <Wrench className="h-4 w-4" />
          Services
        </Button>
        <Button
          variant={section === "gallery" ? "default" : "outline"}
          size="sm"
          onClick={() => setSection("gallery")}
          className="gap-2"
          data-testid="site-editor-section-gallery"
        >
          <ImageIcon className="h-4 w-4" />
          Gallery
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : section === "gallery" ? (
        <GalleryManager authHeaders={authHeaders} />
      ) : section === "services" ? (
        <ServicesManager authHeaders={authHeaders} />
      ) : (
        <div className="space-y-4">
          {currentConfig.blocks.map((blockDef) => {
            const record = blocks[blockDef.key] || { value: "", type: blockDef.type, is_default: true };
            const currentValue = dirty[blockDef.key] !== undefined ? dirty[blockDef.key] : record.value;
            return (
              <Card key={blockDef.key} data-testid={`block-${blockDef.key}`}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <CardTitle className="text-base font-medium">{blockDef.label}</CardTitle>
                    <div className="flex items-center gap-2">
                      <AIWordingButton
                        value={currentValue}
                        onApply={(val) => handleBlockChange(blockDef.key, val)}
                        context={`website content block: ${blockDef.label}`}
                        isHtml={blockDef.type !== "text"}
                        testId={`ai-block-${blockDef.key}`}
                      />
                      {record.is_default && !dirty[blockDef.key] && (
                        <Badge variant="outline" className="text-xs">Default</Badge>
                      )}
                      {dirty[blockDef.key] !== undefined && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">Unsaved</Badge>
                      )}
                    </div>
                  </div>
                  {blockDef.helper && (
                    <p className="text-xs text-muted-foreground">{blockDef.helper}</p>
                  )}
                </CardHeader>
                <CardContent>
                  {blockDef.type === "text" ? (
                    <Input
                      value={currentValue}
                      onChange={(e) => handleBlockChange(blockDef.key, e.target.value)}
                      data-testid={`block-input-${blockDef.key}`}
                    />
                  ) : (
                    <RichTextEditor
                      value={currentValue}
                      onChange={(val) => handleBlockChange(blockDef.key, val)}
                      testId={`block-editor-${blockDef.key}`}
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ==================== Gallery Manager ====================

const GalleryManager = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null); // item object or { _new: true }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiWriting, setAiWriting] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState(GALLERY_CATEGORIES);

  // Category options follow the editable services so gallery projects can be
  // tagged with any service category (new services appear automatically).
  useEffect(() => {
    axios.get(`${API}/admin/site/services`, authHeaders).then((res) => {
      const services = res.data?.items || [];
      if (!services.length) return;
      const seen = new Set();
      const opts = [];
      services.forEach((s) => {
        const v = (s.category || "").trim();
        if (!v || seen.has(v)) return;
        seen.add(v);
        opts.push({ value: v, label: s.title });
      });
      opts.push({ value: "other", label: "Other" });
      setCategoryOptions(opts);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autoWriteGalleryCopy = async () => {
    if (!editing?.title?.trim()) {
      toast.error("Add a title first — the AI writes from it");
      return;
    }
    setAiWriting(true);
    try {
      const res = await axios.post(`${API}/admin/ai/gallery-copy`, {
        title: editing.title,
        category: editing.category || "",
      }, authHeaders);
      setEditing((p) => ({
        ...p,
        description: res.data.description || p.description,
        meta_description: (res.data.meta_description || p.meta_description || "").slice(0, 160),
      }));
      toast.success("Description & SEO meta written — tweak anything, then save");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI writing failed — try again");
    } finally {
      setAiWriting(false);
    }
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/gallery`, authHeaders);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load gallery");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((i) => i.status === statusFilter);
  }, [items, statusFilter]);

  const openNew = () => setEditing({
    _new: true,
    title: "",
    description: "",
    category: "carpentry",
    photos: [],
    status: "draft",
  });

  const saveItem = async () => {
    if (!editing.title?.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (editing._new) {
        await axios.post(`${API}/admin/gallery`, {
          title: editing.title,
          description: editing.description || "",
          category: editing.category,
          photos: editing.photos || [],
          status: editing.status || "draft",
          slug: editing.slug || null,
          meta_description: editing.meta_description || "",
        }, authHeaders);
        toast.success("Gallery item created");
      } else {
        await axios.patch(`${API}/admin/gallery/${editing.id}`, {
          title: editing.title,
          description: editing.description,
          category: editing.category,
          photos: editing.photos,
          status: editing.status,
          slug: editing.slug,
          meta_description: editing.meta_description || "",
        }, authHeaders);
        toast.success("Gallery item updated");
      }
      setEditing(null);
      await fetchItems();
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/admin/gallery/${item.id}`, { status: next }, authHeaders);
      toast.success(next === "published" ? "Published to site" : "Reverted to draft");
      await fetchItems();
    } catch (e) {
      console.error(e);
      toast.error("Status update failed");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/gallery/${item.id}`, authHeaders);
      toast.success("Deleted");
      await fetchItems();
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  };

  const [bulkPhotoOpen, setBulkPhotoOpen] = useState(false);

  return (
    <div className="space-y-4" data-testid="gallery-manager">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]" data-testid="gallery-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({items.length})</SelectItem>
              <SelectItem value="draft">Drafts ({items.filter((i) => i.status === "draft").length})</SelectItem>
              <SelectItem value="published">Published ({items.filter((i) => i.status === "published").length})</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkPhotoOpen(true)}
            disabled={items.length < 2}
            className="gap-2"
            data-testid="gallery-bulk-photos-btn"
            title={items.length < 2 ? "Need at least 2 gallery items to move photos between them" : ""}
          >
            <ArrowRightLeft className="h-4 w-4" />
            Manage photos
          </Button>
          <Button onClick={openNew} className="gap-2" data-testid="gallery-new-btn">
            <Plus className="h-4 w-4" />
            New project
          </Button>
        </div>
      </div>

      {bulkPhotoOpen && (
        <BulkPhotoManager
          authHeaders={authHeaders}
          items={items}
          onClose={() => setBulkPhotoOpen(false)}
          onDone={() => { setBulkPhotoOpen(false); fetchItems(); }}
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-muted-foreground">
          <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm font-medium">No gallery items yet</p>
          <p className="text-xs">Click "New project" or use "Publish to Gallery" on a completed job to add entries.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((item) => {
            const cover = item.photos?.[0];
            return (
              <Card
                key={item.id}
                className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setEditing({ ...item })}
                data-testid={`gallery-card-${item.id}`}
              >
                <div className="aspect-video bg-muted relative">
                  {cover ? (
                    <img
                      src={cover.startsWith("http") ? cover : `${window.location.origin}${cover}`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full text-muted-foreground">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}
                  <Badge
                    className={`absolute top-2 right-2 ${item.status === "published" ? "bg-green-600 text-white" : "bg-amber-100 text-amber-800 border-amber-300"}`}
                  >
                    {item.status === "published" ? "Published" : "Draft"}
                  </Badge>
                  {item.photos?.length > 1 && (
                    <Badge variant="outline" className="absolute bottom-2 left-2 bg-black/60 text-white border-0">
                      {item.photos.length} photos
                    </Badge>
                  )}
                </div>
                <CardContent className="p-3">
                  <p className="font-medium truncate">{item.title}</p>
                  <p className="text-xs text-muted-foreground capitalize">{(item.category || "").replace("_", " ")}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl" data-testid="gallery-edit-dialog">
            <DialogHeader>
              <DialogTitle>{editing._new ? "New gallery item" : "Edit gallery item"}</DialogTitle>
              <DialogDescription>Upload photos and describe the project. Flip to "Published" when you're ready to go live.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={editing.title || ""}
                  onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Bespoke oak dining table"
                  data-testid="gallery-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing((p) => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="gallery-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(editing.category && !categoryOptions.some((c) => c.value === editing.category)
                        ? [...categoryOptions, { value: editing.category, label: editing.category }]
                        : categoryOptions
                      ).map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing((p) => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="gallery-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Description</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1.5 text-primary border-primary/30 hover:bg-primary/10"
                    onClick={autoWriteGalleryCopy}
                    disabled={aiWriting}
                    data-testid="gallery-ai-autowrite-btn"
                  >
                    {aiWriting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {aiWriting ? "Writing…" : "Auto-write description & SEO"}
                  </Button>
                </div>
                <RichTextEditor
                  value={editing.description || ""}
                  onChange={(val) => setEditing((p) => ({ ...p, description: val }))}
                  placeholder="Tell visitors about the project — materials, challenges, what made it special."
                  testId="gallery-description-editor"
                />
              </div>

              {/* SEO fields */}
              <div className="border-t pt-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">SEO</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-sm">URL slug</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">/gallery/</span>
                      <Input
                        value={editing.slug || ""}
                        onChange={(e) => setEditing((p) => ({ ...p, slug: e.target.value }))}
                        onBlur={(e) => setEditing((p) => ({ ...p, slug: slugify(e.target.value || p.title || "") }))}
                        placeholder={slugify(editing.title || "")}
                        data-testid="gallery-slug-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Auto-generated from title. Letters, numbers and hyphens only.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Meta description</Label>
                      <AIWordingButton
                        value={editing.meta_description || ""}
                        onApply={(t) => setEditing((p) => ({ ...p, meta_description: t.slice(0, 160) }))}
                        context="a Google search meta description for a gallery project page (must be under 160 characters)"
                        testId="ai-gallery-meta-description"
                      />
                    </div>
                    <Textarea
                      value={editing.meta_description || ""}
                      onChange={(e) => setEditing((p) => ({ ...p, meta_description: e.target.value.slice(0, 160) }))}
                      placeholder="Short summary shown by Google (≤160 chars)."
                      rows={2}
                      data-testid="gallery-meta-description-input"
                    />
                    <p className="text-xs text-muted-foreground mt-0.5">{(editing.meta_description || "").length}/160</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Photos</Label>
                <ImageUploader
                  value={editing.photos || []}
                  onChange={(val) => setEditing((p) => ({ ...p, photos: val }))}
                  authHeaders={authHeaders}
                  folder="gallery"
                  testId="gallery-photo-uploader"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              {!editing._new && (
                <Button variant="destructive" onClick={() => deleteItem(editing)} className="mr-auto gap-1">
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              )}
              {!editing._new && (
                <Button variant="outline" onClick={() => togglePublish(editing)} data-testid="gallery-toggle-publish-btn">
                  {editing.status === "published" ? "Revert to draft" : "Publish"}
                </Button>
              )}
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveItem} disabled={saving} data-testid="gallery-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


// ==================== Bulk Photo Manager ====================

const BulkPhotoManager = ({ authHeaders, items, onClose, onDone }) => {
  const [selected, setSelected] = useState([]); // [{item_id, url}]
  const [target, setTarget] = useState("");
  const [moving, setMoving] = useState(false);

  const toggle = (itemId, url) => {
    const key = `${itemId}::${url}`;
    setSelected((prev) => {
      const exists = prev.find((s) => `${s.item_id}::${s.url}` === key);
      if (exists) return prev.filter((s) => `${s.item_id}::${s.url}` !== key);
      return [...prev, { item_id: itemId, url }];
    });
  };

  const move = async () => {
    if (!target) { toast.error("Select a destination gallery item"); return; }
    if (!selected.length) { toast.error("Select at least one photo"); return; }
    const bySrc = selected.reduce((acc, s) => {
      if (s.item_id === target) return acc;  // skip same-item
      acc[s.item_id] = acc[s.item_id] || [];
      acc[s.item_id].push(s.url);
      return acc;
    }, {});
    if (!Object.keys(bySrc).length) { toast.error("Choose photos from a different item than the destination"); return; }
    setMoving(true);
    try {
      let totalMoved = 0;
      for (const [fromId, urls] of Object.entries(bySrc)) {
        const res = await axios.post(`${API}/admin/gallery/bulk-move-photos`, {
          from_item_id: fromId,
          to_item_id: target,
          photo_urls: urls,
        }, authHeaders);
        totalMoved += res.data?.moved || 0;
      }
      toast.success(`Moved ${totalMoved} photo${totalMoved === 1 ? "" : "s"}`);
      onDone?.();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Move failed");
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-photo-dialog">
        <DialogHeader>
          <DialogTitle>Manage photos across gallery items</DialogTitle>
          <DialogDescription>
            Select photos from any item, pick a destination below, and hit Move. Useful when a photo was uploaded to the wrong gallery entry.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto space-y-4 pr-1">
          {items.map((item) => (
            (item.photos?.length || 0) === 0 ? null : (
              <div key={item.id} className="border rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-sm">{item.title}</p>
                  <Badge variant="outline" className="text-xs capitalize">{(item.category || "").replace("_", " ")}</Badge>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {(item.photos || []).map((url) => {
                    const key = `${item.id}::${url}`;
                    const isSelected = !!selected.find((s) => `${s.item_id}::${s.url}` === key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggle(item.id, url)}
                        className={`relative aspect-square rounded overflow-hidden border-2 transition-colors ${isSelected ? "border-primary ring-2 ring-primary/30" : "border-transparent hover:border-muted-foreground/40"}`}
                        data-testid="bulk-photo-tile"
                      >
                        <img
                          src={url.startsWith("http") ? url : `${window.location.origin}${url}`}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {isSelected && (
                          <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">✓</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )
          ))}
          {items.every((i) => !(i.photos || []).length) && (
            <div className="text-sm text-muted-foreground text-center py-8">No photos in any gallery item yet.</div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex-1 flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap">Move to:</Label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="bulk-photo-target-select"><SelectValue placeholder="Choose destination item" /></SelectTrigger>
              <SelectContent>
                {items.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={move} disabled={moving || selected.length === 0 || !target} data-testid="bulk-photo-move-btn">
              {moving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-1" />}
              Move {selected.length > 0 ? `(${selected.length})` : ""}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ==================== Services Manager ====================

const SERVICE_ICONS = ["Hammer", "Wrench", "Armchair", "PaintBucket", "Drill", "Ruler", "TreePine", "Home"];

const ServicesManager = ({ authHeaders }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/site/services`, authHeaders);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openNew = () => setEditing({
    _new: true,
    title: "",
    description: "",
    image_url: "",
    icon: "Hammer",
    category: "carpentry",
    display_order: (items.reduce((m, i) => Math.max(m, i.display_order || 0), 0) + 10),
    status: "published",
    slug: "",
    long_description: "",
    meta_description: "",
  });

  const save = async () => {
    if (!editing.title?.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        description: editing.description || "",
        image_url: editing.image_url || null,
        photos: editing.photos || [],
        icon: editing.icon,
        category: editing.category,
        display_order: Number(editing.display_order) || 0,
        status: editing.status,
      };
      if (editing._new) {
        await axios.post(`${API}/admin/site/services`, payload, authHeaders);
        toast.success("Service added");
      } else {
        await axios.patch(`${API}/admin/site/services/${editing.id}`, payload, authHeaders);
        toast.success("Service updated");
      }
      setEditing(null);
      await fetchItems();
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    const next = item.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/admin/site/services/${item.id}`, { status: next }, authHeaders);
      toast.success(next === "published" ? "Published" : "Reverted to draft");
      await fetchItems();
    } catch (e) {
      toast.error("Status update failed");
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete service "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/site/services/${item.id}`, authHeaders);
      toast.success("Deleted");
      await fetchItems();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const reorder = async (item, direction) => {
    const sorted = [...items].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const idx = sorted.findIndex((i) => i.id === item.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    try {
      await Promise.all([
        axios.patch(`${API}/admin/site/services/${item.id}`, { display_order: other.display_order }, authHeaders),
        axios.patch(`${API}/admin/site/services/${other.id}`, { display_order: item.display_order }, authHeaders),
      ]);
      await fetchItems();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="services-manager">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">Services shown on the homepage. Use arrows to reorder, toggle Draft/Published to control visibility.</p>
        <Button onClick={openNew} className="gap-2" data-testid="services-new-btn">
          <Plus className="h-4 w-4" />
          New service
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border rounded-lg p-8 text-center text-muted-foreground">No services yet.</div>
      ) : (
        <div className="space-y-2">
          {[...items].sort((a,b) => (a.display_order||0) - (b.display_order||0)).map((item, idx, arr) => (
            <Card key={item.id} className="p-3" data-testid={`service-row-${item.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={idx === 0} onClick={() => reorder(item, -1)} aria-label="Move up">▲</button>
                  <button type="button" className="text-muted-foreground hover:text-foreground disabled:opacity-30" disabled={idx === arr.length - 1} onClick={() => reorder(item, 1)} aria-label="Move down">▼</button>
                </div>
                <div className="w-14 h-14 rounded overflow-hidden bg-muted flex-shrink-0">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Wrench className="h-5 w-5" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium truncate">{item.title}</p>
                    <Badge variant="outline" className="text-xs">#{item.display_order}</Badge>
                    <Badge className={item.status === "published" ? "bg-green-600 text-white text-xs" : "bg-amber-100 text-amber-800 text-xs"}>{item.status}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground truncate" dangerouslySetInnerHTML={{__html: item.description || ""}} />
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {item.slug && item.status === "published" && (
                    <a href={`/services/${item.slug}`} target="_blank" rel="noopener noreferrer" title="View live page" data-testid={`service-view-${item.id}`}>
                      <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                    </a>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => togglePublish(item)} data-testid={`service-toggle-${item.id}`}>
                    {item.status === "published" ? "Unpublish" : "Publish"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing({...item})} data-testid={`service-edit-${item.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteItem(item)} className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <Dialog open onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="service-edit-dialog">
            <DialogHeader>
              <DialogTitle>{editing._new ? "New service" : "Edit service"}</DialogTitle>
              <DialogDescription>Homepage service card — image, title, description.</DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <div>
                <Label>Title *</Label>
                <Input value={editing.title} onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))} data-testid="service-title-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Input value={editing.category} onChange={(e) => setEditing((p) => ({ ...p, category: e.target.value }))} placeholder="e.g. fencing-gates" />
                  <p className="text-xs text-muted-foreground mt-1">Groups gallery projects and drives the website's project filter chips.</p>
                </div>
                <div>
                  <Label>Icon</Label>
                  <Select value={editing.icon} onValueChange={(v) => setEditing((p) => ({ ...p, icon: v }))}>
                    <SelectTrigger data-testid="service-icon-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_ICONS.map((i) => (
                        <SelectItem key={i} value={i}>{i}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Display order</Label>
                  <Input type="number" value={editing.display_order} onChange={(e) => setEditing((p) => ({ ...p, display_order: e.target.value }))} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing((p) => ({ ...p, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <RichTextEditor
                  value={editing.description || ""}
                  onChange={(val) => setEditing((p) => ({ ...p, description: val }))}
                  placeholder="Short description shown under the title."
                  testId="service-description-editor"
                  minHeight={80}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Full page description</Label>
                  <AIWordingButton
                    value={editing.long_description || ""}
                    onApply={(val) => setEditing((p) => ({ ...p, long_description: val }))}
                    context={`the long-form description for the "${editing.title || "service"}" service landing page of a sustainable handyman business in Abergavenny (2-3 paragraphs, local SEO friendly)`}
                    isHtml
                    testId="ai-service-long-description"
                  />
                </div>
                <p className="text-xs text-muted-foreground mb-1">Shown on this service's own landing page (/services/…). Optional.</p>
                <RichTextEditor
                  value={editing.long_description || ""}
                  onChange={(val) => setEditing((p) => ({ ...p, long_description: val }))}
                  placeholder="Longer story for the service page — what you do, materials, process, areas covered."
                  testId="service-long-description-editor"
                  minHeight={120}
                />
              </div>

              {/* SEO fields */}
              <div className="border-t pt-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Landing page & SEO</p>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label className="text-sm">URL slug</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">/services/</span>
                      <Input
                        value={editing.slug || ""}
                        onChange={(e) => setEditing((p) => ({ ...p, slug: e.target.value }))}
                        onBlur={(e) => setEditing((p) => ({ ...p, slug: slugify(e.target.value || p.title || "") }))}
                        placeholder={slugify(editing.title || "")}
                        data-testid="service-slug-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">Auto-generated from title. Letters, numbers and hyphens only.</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Meta description</Label>
                      <AIWordingButton
                        value={editing.meta_description || ""}
                        onApply={(t) => setEditing((p) => ({ ...p, meta_description: t.slice(0, 160) }))}
                        context={`a Google search meta description for the "${editing.title || "service"}" landing page of a sustainable handyman business in Abergavenny (must be under 160 characters)`}
                        testId="ai-service-meta-description"
                      />
                    </div>
                    <Textarea
                      value={editing.meta_description || ""}
                      onChange={(e) => setEditing((p) => ({ ...p, meta_description: e.target.value.slice(0, 160) }))}
                      placeholder="Short summary shown by Google (≤160 chars)."
                      rows={2}
                      data-testid="service-meta-description-input"
                    />
                    <p className="text-xs text-muted-foreground mt-0.5">{(editing.meta_description || "").length}/160</p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Hero image</Label>
                <ImageUploader
                  value={editing.image_url ? [editing.image_url] : []}
                  onChange={(val) => setEditing((p) => ({ ...p, image_url: val[val.length - 1] || "" }))}
                  authHeaders={authHeaders}
                  folder="services"
                  maxPhotos={1}
                  testId="service-image-uploader"
                />
              </div>

              <div>
                <Label>Photo gallery</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  Shown when visitors tap this service on the website. Upload several at once — the hero image above stays as the card cover.
                </p>
                <ImageUploader
                  value={editing.photos || []}
                  onChange={(val) => setEditing((p) => ({ ...p, photos: val }))}
                  authHeaders={authHeaders}
                  folder="services"
                  testId="service-gallery-uploader"
                />
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={save} disabled={saving} data-testid="service-save-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};


export default SiteEditorTab;
